import { TablePagination } from '@mui/material';
import { Column } from 'primereact/column';
import { TreeTable } from 'primereact/treetable';
import React, { useState, useMemo } from 'react';
import { Spinner } from 'react-bootstrap';

const CustomTreeTable = ({
  tableData = [],
  filteredCustomers = [],
  rowsPerPage,
  setRowsPerPage,
  sortField,
  sortOrder,
  handleSortChange,
  currentPage,
  setCurrentPage,
  loading,
  bodyTemplates = [],
  totalRecords = 0,
  checkbox = true,
  pagination = true,
  pageName = '',
}) => {
  const [hoveredRowKey, setHoveredRowKey] = useState(null);
  const [expandedKeys, setExpandedKeys] = useState({});

  const treeNodes = useMemo(() => {
    return filteredCustomers.map((parent) => ({
      key: parent.Id,
      data: parent,
      children:
        parent.cgaChildFiles?.map((child) => ({
          key: child.FileId,
          data: {...child, UserFileName: child.UserFileName || child.Name, isChild: true},
        })) || [],
    }));
  }, [filteredCustomers]);

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setCurrentPage(0);
    setRowsPerPage(parseInt(event.target.value));
  };

  const onToggle = (e) => setExpandedKeys(e.value);

  const toggleRow = (rowId) => {
    setExpandedKeys((prev) => {
      const updatedExpandedKeys = { ...prev };
      if (updatedExpandedKeys[rowId]) {
        delete updatedExpandedKeys[rowId]; // If already expanded, collapse it
      } else {
        updatedExpandedKeys[rowId] = true; // Expand it
      }
      return updatedExpandedKeys;
    });
  };

  return (
    <>
      {/* ==== Loading State ==== */}
      {loading && (
        <TreeTable
          value={[]}
          removableSort
          tableStyle={{
            minHeight: pageName === 'Dashboard' ? '330px' : '400px',
          }}
          emptyMessage={
            <div className="d-flex justify-content-center align-items-center" style={{ height: '100%' }}>
              <Spinner animation="border" />
            </div>
          }
        >
          {tableData.map((col, index) => (
            <Column key={index} header={col.header} className={col?.className} />
          ))}
        </TreeTable>
      )}

      {/* ==== No Data ==== */}
      {!loading && (!filteredCustomers || filteredCustomers.length === 0) && (
        <TreeTable
          value={[]}
          removableSort
          tableStyle={{
            minHeight: pageName === 'Dashboard' ? '330px' : '400px',
          }}
          emptyMessage={<div className="text-center fs-6">No Data Found</div>}
        >
          {tableData.map((col, index) => (
            <Column key={index} header={col.header} style={col.style ? col.style : {}} sortable />
          ))}
        </TreeTable>
      )}

      {/* ==== Actual TreeTable ==== */}
      {!loading && filteredCustomers?.length > 0 && (
        <span>
          <TreeTable
            value={treeNodes}
            rows={rowsPerPage}
            expandedKeys={expandedKeys}
            first={currentPage * rowsPerPage}
            tableStyle={{ minWidth: '50rem' }}
            sortField={sortField}
            sortOrder={sortOrder === 0 ? 1 : sortOrder}
            onSort={handleSortChange}
            scrollable
            onRowMouseEnter={(e) => setHoveredRowKey(e.node.key)}
            onRowMouseLeave={() => setHoveredRowKey(null)}
            onToggle={onToggle}
          >
            {/* ==== Checkbox column ==== */}
            {checkbox && <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />}

            {/* ==== Dynamic Columns ==== */}
            {tableData.map((col, index) => (
              <Column
                key={index}
                alignFrozen={col?.alignFrozen}
                frozen={col?.frozen === true}
                field={col.field}
                header={col.header}
                headerClassName={col?.frozen ? 'custom-header-bg' : ''}
                className={col?.className}
                sortable={index === 0 ? false : col?.sortable}
                body={(node) =>
                  bodyTemplates[col.field]
                    ? bodyTemplates[col.field](node.data, {
                      isRowHovered: hoveredRowKey === node.key,
                      expandedRows: Object.keys(expandedKeys),
                      toggleRow: (rowId) =>
                        toggleRow(rowId),
                    })
                    : node.data[col.field]
                }
              />
            ))}

          </TreeTable>

          {/* ==== Pagination ==== */}
          {pagination && (
            <div className="mt-1">
              <TablePagination
                component="div"
                count={totalRecords}
                page={currentPage}
                rowsPerPageOptions={[5, 10, 20, 25]}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </div>
          )}
        </span>
      )}
    </>
  );
};

export default CustomTreeTable;
